import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Package, FileText,
  Download, Calendar, ChevronDown, Eye, Filter
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const profitabilityData = [
  {
    id: 'MAN-2026-02-28-001',
    date: '2026-02-28',
    totalCost: 202250,
    totalRevenue: 224399,
    profitDeficit: 22149,
    margin: 9.87,
    bags: 12,
    singles: 45,
    weight: '342.5 lb',
    status: 'profit'
  },
  {
    id: 'MAN-2026-02-27-003',
    date: '2026-02-27',
    totalCost: 145800,
    totalRevenue: 162500,
    profitDeficit: 16700,
    margin: 10.28,
    bags: 8,
    singles: 32,
    weight: '256.2 lb',
    status: 'profit'
  },
  {
    id: 'MAN-2026-02-26-001',
    date: '2026-02-26',
    totalCost: 312400,
    totalRevenue: 305200,
    profitDeficit: -7200,
    margin: -2.36,
    bags: 15,
    singles: 67,
    weight: '478.3 lb',
    status: 'deficit'
  },
  {
    id: 'MAN-2026-02-25-002',
    date: '2026-02-25',
    totalCost: 189500,
    totalRevenue: 218400,
    profitDeficit: 28900,
    margin: 13.23,
    bags: 10,
    singles: 41,
    weight: '312.1 lb',
    status: 'profit'
  },
];

const trendData = [
  { date: 'Feb 20', profit: 15200, margin: 8.5 },
  { date: 'Feb 21', profit: 22400, margin: 11.2 },
  { date: 'Feb 22', profit: -5600, margin: -2.8 },
  { date: 'Feb 23', profit: 18900, margin: 9.4 },
  { date: 'Feb 24', profit: 31200, margin: 14.1 },
  { date: 'Feb 25', profit: 28900, margin: 13.2 },
  { date: 'Feb 26', profit: -7200, margin: -2.4 },
  { date: 'Feb 27', profit: 16700, margin: 10.3 },
  { date: 'Feb 28', profit: 22149, margin: 9.9 },
];

const costBreakdown = [
  { name: 'Customs Duty', value: 125000, color: '#3b82f6' },
  { name: 'GCT', value: 18750, color: '#06b6d4' },
  { name: 'Freight', value: 45000, color: '#84cc16' },
  { name: 'Handling', value: 8500, color: '#f59e0b' },
  { name: 'Processing', value: 5000, color: '#f43f5e' },
];

export default function Profitability() {
  const [dateRange, setDateRange] = useState('7d');
  const [selectedReport, setSelectedReport] = useState(null);

  const totalRevenue = profitabilityData.reduce((sum, d) => sum + d.totalRevenue, 0);
  const totalCost = profitabilityData.reduce((sum, d) => sum + d.totalCost, 0);
  const totalProfit = totalRevenue - totalCost;
  const avgMargin = (totalProfit / totalRevenue * 100).toFixed(1);
  const profitableManifests = profitabilityData.filter(d => d.status === 'profit').length;

  return (
    <div className="profitability-page" data-testid="profitability-page">
      {/* Summary Cards */}
      <div className="profit-summary-grid">
        <div className="profit-card revenue">
          <div className="profit-icon">
            <TrendingUp size={24} />
          </div>
          <div className="profit-content">
            <span className="profit-label">Total Revenue</span>
            <span className="profit-value">${totalRevenue.toLocaleString()}</span>
            <span className="profit-period">Last 7 days</span>
          </div>
        </div>
        <div className="profit-card cost">
          <div className="profit-icon">
            <DollarSign size={24} />
          </div>
          <div className="profit-content">
            <span className="profit-label">Total Cost</span>
            <span className="profit-value">${totalCost.toLocaleString()}</span>
            <span className="profit-period">Last 7 days</span>
          </div>
        </div>
        <div className={`profit-card ${totalProfit >= 0 ? 'profit' : 'deficit'}`}>
          <div className="profit-icon">
            {totalProfit >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
          </div>
          <div className="profit-content">
            <span className="profit-label">Net Profit</span>
            <span className="profit-value">${Math.abs(totalProfit).toLocaleString()}</span>
            <span className="profit-margin">{avgMargin}% margin</span>
          </div>
        </div>
        <div className="profit-card success-rate">
          <div className="profit-icon">
            <Package size={24} />
          </div>
          <div className="profit-content">
            <span className="profit-label">Profitable Manifests</span>
            <span className="profit-value">{profitableManifests}/{profitabilityData.length}</span>
            <span className="profit-period">{Math.round(profitableManifests/profitabilityData.length*100)}% success rate</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="profit-charts-grid">
        <div className="chart-card large">
          <div className="chart-header">
            <h3>Profit Trend</h3>
            <div className="date-range-selector">
              <button 
                className={`range-btn ${dateRange === '7d' ? 'active' : ''}`}
                onClick={() => setDateRange('7d')}
              >
                7D
              </button>
              <button 
                className={`range-btn ${dateRange === '30d' ? 'active' : ''}`}
                onClick={() => setDateRange('30d')}
              >
                30D
              </button>
              <button 
                className={`range-btn ${dateRange === '90d' ? 'active' : ''}`}
                onClick={() => setDateRange('90d')}
              >
                90D
              </button>
            </div>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#84cc16" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#84cc16" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                <Tooltip 
                  contentStyle={{ 
                    background: '#1e293b', 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => [
                    name === 'profit' ? `$${value.toLocaleString()}` : `${value}%`,
                    name === 'profit' ? 'Profit' : 'Margin'
                  ]}
                />
                <Area 
                  type="monotone" 
                  dataKey="profit" 
                  stroke="#84cc16" 
                  fillOpacity={1} 
                  fill="url(#colorProfit)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Cost Breakdown</h3>
            <span className="chart-subtitle">Last manifest</span>
          </div>
          <div className="chart-body pie-chart">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={costBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {costBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    background: '#1e293b', 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`$${value.toLocaleString()}`, '']}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => <span style={{ color: '#94a3b8' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Manifest Profitability Table */}
      <div className="data-table-card">
        <div className="table-header">
          <h3>Manifest Profitability</h3>
          <button className="btn btn-secondary" data-testid="export-report-btn">
            <Download size={14} /> Export Report
          </button>
        </div>
        <table className="data-table" data-testid="profitability-table">
          <thead>
            <tr>
              <th>Manifest</th>
              <th>Date</th>
              <th>Items</th>
              <th>Cost</th>
              <th>Revenue</th>
              <th>Profit/Deficit</th>
              <th>Margin</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {profitabilityData.map((report) => (
              <tr key={report.id} data-testid={`report-row-${report.id}`}>
                <td>
                  <span className="manifest-id">{report.id}</span>
                </td>
                <td>{report.date}</td>
                <td>
                  <span className="items-summary">
                    {report.bags} bags, {report.singles} singles
                  </span>
                </td>
                <td className="cost-cell">${report.totalCost.toLocaleString()}</td>
                <td className="revenue-cell">${report.totalRevenue.toLocaleString()}</td>
                <td>
                  <span className={`profit-amount ${report.status}`}>
                    {report.status === 'profit' ? '+' : ''}${report.profitDeficit.toLocaleString()}
                  </span>
                </td>
                <td>
                  <span className={`margin-badge ${report.status}`}>
                    {report.status === 'profit' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {report.margin}%
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn" 
                      title="View Details"
                      onClick={() => setSelectedReport(report)}
                      data-testid={`view-${report.id}`}
                    >
                      <Eye size={16} />
                    </button>
                    <button className="action-btn" title="Export" data-testid={`export-${report.id}`}>
                      <Download size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedReport && (
        <div className="modal-overlay" onClick={() => setSelectedReport(null)}>
          <div className="modal-content profit-modal" onClick={e => e.stopPropagation()} data-testid="profit-detail-modal">
            <div className="modal-header">
              <div>
                <h3>Profitability Report</h3>
                <span className="modal-subtitle">{selectedReport.id}</span>
              </div>
              <button className="close-btn" onClick={() => setSelectedReport(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="report-summary">
                <div className="summary-row">
                  <span className="label">Date:</span>
                  <span className="value">{selectedReport.date}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Total Weight:</span>
                  <span className="value">{selectedReport.weight}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Items:</span>
                  <span className="value">{selectedReport.bags} bags, {selectedReport.singles} singles</span>
                </div>
              </div>

              <div className="cost-revenue-comparison">
                <div className="comparison-section costs">
                  <h4>Shipment Costs (What KP Paid)</h4>
                  <div className="line-items">
                    <div className="line-item">
                      <span>Customs Duty</span>
                      <span>$125,000.00</span>
                    </div>
                    <div className="line-item">
                      <span>GCT</span>
                      <span>$18,750.00</span>
                    </div>
                    <div className="line-item">
                      <span>Customs Processing Fee</span>
                      <span>$5,000.00</span>
                    </div>
                    <div className="line-item">
                      <span>Freight (Airline)</span>
                      <span>$45,000.00</span>
                    </div>
                    <div className="line-item">
                      <span>Warehouse Handling</span>
                      <span>$8,500.00</span>
                    </div>
                    <div className="line-item total">
                      <span>Total Cost</span>
                      <span>${selectedReport.totalCost.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="comparison-section revenue">
                  <h4>Invoiced Revenue (What Couriers Pay)</h4>
                  <div className="line-items">
                    <div className="line-item">
                      <span>FastShip Jamaica</span>
                      <span>$85,420.00</span>
                    </div>
                    <div className="line-item">
                      <span>SpeedyCourier</span>
                      <span>$72,150.00</span>
                    </div>
                    <div className="line-item">
                      <span>IslandExpress</span>
                      <span>$38,029.00</span>
                    </div>
                    <div className="line-item">
                      <span>KP Direct Customers</span>
                      <span>$28,800.00</span>
                    </div>
                    <div className="line-item total">
                      <span>Total Revenue</span>
                      <span>${selectedReport.totalRevenue.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`profit-result ${selectedReport.status}`}>
                <div className="result-header">
                  {selectedReport.status === 'profit' ? (
                    <TrendingUp size={28} />
                  ) : (
                    <TrendingDown size={28} />
                  )}
                  <h3>{selectedReport.status === 'profit' ? 'NET PROFIT' : 'NET DEFICIT'}</h3>
                </div>
                <div className="result-amount">
                  ${Math.abs(selectedReport.profitDeficit).toLocaleString()}
                </div>
                <div className="result-margin">
                  {selectedReport.margin}% margin
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedReport(null)}>
                Close
              </button>
              <button className="btn btn-secondary">
                <Download size={14} /> Export PDF
              </button>
              <button className="btn btn-primary">
                <FileText size={14} /> Export Excel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
